import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Clock, Copy, Inbox, ListChecks, Lock, Plus } from 'lucide-react';
import { z } from 'zod';
import { addDays, parseDuration, today, weekStart, type z as Z } from '@mkcore/shared';
import { closeDays, reopenDays, setWeekCell, type Week as WeekSchema } from '@mkcore/time/schema';
import { Badge, Button, buttonVariants, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, EmptyState, ErrorState, Field, PageHeader, Skeleton, cn, toast } from '@mkcore/ui';
import { formatDuration } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan, useMe } from '@/lib/session';
import { Dot, ProjectSelect, WorkTypeSelect, useBookableProjects, useWorkTypes } from '@/features/time/pickers';
import { weekQuery } from '@/features/time/queries';
import { isoWeekNumber } from '@/features/time/util';

type Week = Z.output<typeof WeekSchema>;
type Row = Week['rows'][number];

const search = z.object({ date: z.string().optional() });
export const Route = createFileRoute('/_app/time/week')({ validateSearch: search, component: WeekGrid });

/** One editable cell: type a duration, Enter or leaving the cell saves, arrows move between cells (CO-0212). */
function Cell({ cell, col, rowIndex, label, disabled, onSet }: { cell: Row['cells'][number]; col: number; rowIndex: number; label: string; disabled: boolean; onSet: (seconds: number) => Promise<unknown> }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string | null>(null);
  // While the save runs the typed text stays visible; Enter followed by blur must not save twice.
  const [pending, setPending] = useState<string | null>(null);
  const busyRef = useRef(false);
  const busy = pending !== null;
  const shown = draft ?? pending ?? (cell.seconds ? formatDuration(cell.seconds) : '');
  const commit = async () => {
    if (draft === null || busyRef.current) return;
    const text = draft.trim();
    setDraft(null);
    const seconds = text ? parseDuration(text) : 0;
    if (seconds === null) {
      toast.error(t('time.week.unreadable'));
      return;
    }
    if (seconds === cell.seconds) return;
    busyRef.current = true;
    setPending(seconds ? formatDuration(seconds) : '');
    try {
      await onSet(seconds);
    } finally {
      busyRef.current = false;
      setPending(null);
    }
  };
  const move = (e: KeyboardEvent<HTMLInputElement>, dr: number, dc: number) => {
    const next = document.querySelector<HTMLInputElement>(`[data-cell="${rowIndex + dr}:${col + dc}"]`);
    if (next) {
      e.preventDefault();
      next.focus();
    }
  };
  const title = cell.locked ? t('time.week.locked') : cell.timedSeconds ? t('time.week.hasTimed', { duration: formatDuration(cell.timedSeconds) }) : undefined;
  return (
    <div className="relative">
      <input
        data-cell={`${rowIndex}:${col}`}
        className={cn(
          'h-9 w-full min-w-16 rounded-sm border border-transparent bg-transparent px-1 text-right text-sm tabular-nums hover:border-line focus:border-line-strong focus:bg-surface focus:outline-none',
          cell.seconds === 0 && 'text-fg-3',
          busy && 'opacity-60',
        )}
        aria-label={label}
        title={title}
        value={shown}
        disabled={disabled || cell.locked || busy}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            void commit();
            move(e, 0, 1);
          } else if (e.key === 'Escape') setDraft(null);
          else if (e.key === 'ArrowUp') move(e, -1, 0);
          else if (e.key === 'ArrowDown') move(e, 1, 0);
          else if (e.key === 'ArrowLeft' && draft === null) move(e, 0, -1);
          else if (e.key === 'ArrowRight' && draft === null) move(e, 0, 1);
        }}
      />
      {cell.locked ? (
        <Lock className="pointer-events-none absolute left-1 top-2.5 size-3 text-fg-4" aria-hidden />
      ) : cell.timedSeconds > 0 ? (
        <Clock className="pointer-events-none absolute left-1 top-2.5 size-3 text-fg-4" aria-hidden />
      ) : null}
    </div>
  );
}

function AddRowDialog({ open, onClose, onAdd, existing }: { open: boolean; onClose: () => void; onAdd: (projectId: string, workTypeId: string) => void; existing: Set<string> }) {
  const { t } = useTranslation();
  const [projectId, setProjectId] = useState('');
  const [workTypeId, setWorkTypeId] = useState('');
  useEffect(() => {
    setProjectId('');
    setWorkTypeId('');
  }, [open]);
  const dup = !!projectId && !!workTypeId && existing.has(`${projectId}:${workTypeId}`);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('time.week.addRowTitle')}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label={t('time.fields.project')}>{(p) => <ProjectSelect {...p} autoFocus value={projectId} onChange={setProjectId} />}</Field>
          <Field label={t('time.fields.workType')} error={dup ? t('time.week.rowExists') : undefined}>{(p) => <WorkTypeSelect {...p} value={workTypeId} onChange={setWorkTypeId} />}</Field>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={!projectId || !workTypeId || dup} onClick={() => onAdd(projectId, workTypeId)}>
            {t('common.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The week grid: rows of project and work type, a duration per day, row and day totals and the goal (CO-0212). */
function WeekGrid() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const qc = useQueryClient();
  const message = useErrorMessage();
  const canCreate = useCan('time:create');
  const startsOn = useMe().weekStart;
  const monday = weekStart(s.date ?? today(), startsOn);
  const week = useQuery(weekQuery(monday, undefined, startsOn));
  const projects = useBookableProjects();
  const workTypes = useWorkTypes();
  const [extra, setExtra] = useState<{ projectId: string; workTypeId: string }[]>([]);
  const [adding, setAdding] = useState(false);
  useEffect(() => setExtra([]), [monday]);
  const setCell = useActionMutation(setWeekCell, {
    invalidate: [['time', 'entry']],
    silentErrors: true,
    onSuccess: (w) => qc.setQueryData(weekQuery(monday, undefined, startsOn).queryKey, w),
  });
  const go = (date: string | undefined) => void navigate({ search: date ? { date } : {}, replace: true });
  // Close the week as complete (CO-0224): its entries become confirmed and agents propose no concept hours for it.
  const close = useActionMutation(closeDays, { invalidate: [['time']], success: t('time.week.closedDone') });
  const reopen = useActionMutation(reopenDays, { invalidate: [['time']], success: t('time.week.reopenedDone') });

  if (week.isError) return <ErrorState title={t('errors.generic')} description={message(week.error)} />;
  if (!week.data) return <Skeleton className="h-96" />;
  const w = week.data;
  const serverKeys = new Set(w.rows.map((r) => r.key));
  const extraRows: Row[] = extra
    .filter((x) => !serverKeys.has(`${x.projectId}:${x.workTypeId}`))
    .map((x) => {
      const p = projects.find((pr) => pr.id === x.projectId);
      const wt = workTypes.find((y) => y.id === x.workTypeId);
      return {
        key: `${x.projectId}:${x.workTypeId}`,
        projectId: x.projectId,
        projectName: p?.name ?? '',
        projectColour: p?.colour ?? '#5C6B73',
        organisationName: p?.organisationName ?? null,
        engagementName: p?.engagementName ?? null,
        workTypeId: x.workTypeId,
        workTypeName: wt?.name ?? '',
        cells: w.days.map((date) => ({ date, seconds: 0, timedSeconds: 0, entryIds: [], locked: false })),
        totalSeconds: 0,
      };
    });
  const rows = [...w.rows, ...extraRows];
  const todayDate = today();
  const goal = w.goalSeconds ?? 0;
  const pct = goal ? Math.min(100, Math.round((w.totalSeconds / goal) * 100)) : 0;
  const dayName = (i: number) => t(`workSettings.day.${((startsOn - 1 + i) % 7) + 1}`);

  return (
    <>
      <PageHeader
        title={t('time.week.title')}
        description={t('time.week.description')}
        actions={
          canCreate && (
            <Button onClick={() => setAdding(true)}>
              <Plus /> {t('time.week.addRow')}
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="icon" aria-label={t('time.week.prevWeek')} onClick={() => go(addDays(monday, -7))}>
          <ChevronLeft />
        </Button>
        <span className="min-w-56 text-sm font-bold">
          {t('time.week.weekLabel', { week: isoWeekNumber(monday) })} · {monday.slice(8)}–{w.days[6]!.slice(8)}-{w.days[6]!.slice(5, 7)}-{w.days[6]!.slice(0, 4)}
        </span>
        <Button variant="ghost" size="icon" aria-label={t('time.week.nextWeek')} onClick={() => go(addDays(monday, 7))}>
          <ChevronRight />
        </Button>
        <Button variant="secondary" size="sm" onClick={() => go(undefined)} disabled={monday === weekStart(todayDate, startsOn)}>
          {t('time.week.thisWeek')}
        </Button>
        {w.openDrafts > 0 && (
          <Link to="/time/drafts" search={{ from: w.days[0], to: w.days[6] }} className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
            <Inbox /> {t('time.week.drafts', { count: w.openDrafts })}
          </Link>
        )}
        {w.closedDays.length === 7 ? (
          <>
            <Badge tone="success">
              <CheckCircle2 className="size-3.5" aria-hidden /> {t('time.week.closed')}
            </Badge>
            <Button variant="ghost" size="sm" loading={reopen.isPending} onClick={() => reopen.mutate({ input: { from: w.days[0]!, to: w.days[6]! } })}>
              {t('time.week.reopen')}
            </Button>
          </>
        ) : (
          <Button variant="secondary" size="sm" loading={close.isPending} onClick={() => close.mutate({ input: { from: w.days[0]!, to: w.days[6]! } })}>
            <CheckCircle2 /> {t('time.week.close')}
          </Button>
        )}
        <div className="ml-auto flex items-center gap-3 text-sm">
          <span className="text-fg-2">
            {t('time.week.total')}: <span className="font-bold text-fg-1">{formatDuration(w.totalSeconds)}</span>
            {goal ? <span className="text-fg-3"> {t('time.ofGoal', { goal: formatDuration(goal) })}</span> : null}
          </span>
          {goal ? (
            <span className="h-2 w-32 overflow-hidden rounded-full bg-sunken" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={t('time.week.goal')}>
              <span className="block h-full bg-gold" style={{ width: `${pct}%` }} />
            </span>
          ) : null}
        </div>
      </div>
      {!rows.length ? (
        <EmptyState
          title={t('time.week.empty')}
          description={t('time.week.emptyHint')}
          action={
            canCreate && (
              <Button onClick={() => setAdding(true)}>
                <Plus /> {t('time.week.addRow')}
              </Button>
            )
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line-subtle bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line-subtle text-xs uppercase tracking-caps text-fg-3">
                <th className="px-3 py-2 text-left font-bold">{t('time.week.project')}</th>
                {w.days.map((d, i) => (
                  <th key={d} className={cn('px-2 py-2 text-right font-bold', d === todayDate && 'text-gold-strong', !w.workdays.includes(((startsOn - 1 + i) % 7) + 1) && 'text-fg-4')}>
                    <Link to="/time" search={{ date: d }} className="hover:underline">
                      {dayName(i)} {d.slice(8)}
                    </Link>
                    <Link to="/time/day/$date" params={{ date: d }} className="ml-1 inline-flex align-middle text-fg-3 hover:text-fg-1" title={t('time.day.open')} aria-label={t('time.day.openFor', { date: d })}>
                      <ListChecks className="size-3.5" aria-hidden />
                    </Link>
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-bold">{t('time.week.total')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={row.key} className="border-b border-line-subtle last:border-0">
                  <td className="px-3 py-1">
                    <div className="flex min-w-48 items-center gap-2">
                      <Dot colour={row.projectColour} />
                      <div className="min-w-0">
                        <div className="truncate font-bold">{row.projectName}</div>
                        <div className="truncate text-xs text-fg-3">
                          {row.organisationName ?? t('projects.internal')} · {row.workTypeName}
                        </div>
                      </div>
                    </div>
                  </td>
                  {row.cells.map((cell, ci) => (
                    <td key={cell.date} className={cn('px-1 py-1', cell.date === todayDate && 'bg-gold/5')}>
                      <Cell
                        cell={cell}
                        col={ci}
                        rowIndex={ri}
                        label={`${row.projectName} ${row.workTypeName} ${dayName(ci)} ${cell.date.slice(8)}`}
                        disabled={!canCreate}
                        onSet={(seconds) =>
                          setCell
                            .mutateAsync({ input: { date: cell.date, projectId: row.projectId, workTypeId: row.workTypeId, durationSeconds: seconds, weekStartsOn: startsOn } })
                            .catch((e: unknown) => toast.error(message(e)))
                        }
                      />
                    </td>
                  ))}
                  <td className="px-3 py-1 text-right font-bold tabular-nums">{row.totalSeconds ? formatDuration(row.totalSeconds) : ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-line bg-surface-alt text-xs">
                <td className="px-3 py-2 font-bold uppercase tracking-caps text-fg-3">{t('time.week.total')}</td>
                {w.dayTotals.map((sec, i) => (
                  <td key={w.days[i]} className="px-2 py-2 text-right font-bold tabular-nums">
                    {sec ? formatDuration(sec) : ''}
                  </td>
                ))}
                <td className="px-3 py-2 text-right font-bold tabular-nums">{formatDuration(w.totalSeconds)}</td>
              </tr>
              {w.dayChecks.some((c) => c.missingSeconds || c.overlaps || c.unusual.length) && (
                <tr className="text-xs" data-testid="week-checks">
                  <td className="px-3 py-1.5 text-fg-3">{t('time.week.checks')}</td>
                  {w.dayChecks.map((c) => (
                    <td key={c.date} className="px-2 py-1.5 text-right">
                      <span className="inline-flex flex-wrap justify-end gap-1">
                        {c.missingSeconds > 0 && (
                          <span className="rounded-sm bg-warning-bg px-1 tabular-nums text-warning" title={t('time.week.missing', { duration: formatDuration(c.missingSeconds) })}>
                            −{formatDuration(c.missingSeconds)}
                          </span>
                        )}
                        {c.overlaps > 0 && (
                          <Link to="/time" search={{ date: c.date }} className="inline-flex items-center gap-0.5 rounded-sm bg-danger-bg px-1 text-danger hover:underline" title={t('time.week.overlaps', { count: c.overlaps })}>
                            <Copy className="size-3" aria-hidden /> {c.overlaps}
                          </Link>
                        )}
                        {c.unusual.map((u) => (
                          <span key={u} className="inline-flex items-center rounded-sm bg-danger-bg px-1 text-danger" title={t(`time.week.unusual.${u}`)}>
                            <AlertTriangle className="size-3" aria-label={t(`time.week.unusual.${u}`)} />
                          </span>
                        ))}
                      </span>
                    </td>
                  ))}
                  <td />
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      )}
      <AddRowDialog
        open={adding}
        onClose={() => setAdding(false)}
        existing={new Set(rows.map((r) => r.key))}
        onAdd={(projectId, workTypeId) => {
          setExtra((x) => [...x, { projectId, workTypeId }]);
          setAdding(false);
        }}
      />
    </>
  );
}
